import React, {useEffect, useState} from "react"
import authentication from "@irati/auth"
import styled from "styled-components"
import {useLazyUsersForBusiness} from "@irati/identity/hooks"
import {AutomationType, RecognitionAutomation, RecognitionAutomationStatus} from "@irati/types"
import GenericModal from "@irati/ui/src/GenericModal"
import History, {historyContext} from "@irati/utils/history"
import Flex from "@irati/ui/src/Flex"
import {Body, H3, H4, H5} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import {Hr} from "@irati/ui/src/Layout"
import adminTracking from "@irati-tracking/admin/admin"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button"
import {AUTOMATION_BASE, AUTOMATION_LIST, VIEW_PEOPLE} from "../../routes/routeConfig"

export enum Operation {
  CREATE,
  EDIT,
  TOGGLE_STATUS
}
interface Props {
  recognitionAutomation: RecognitionAutomation
  operation: Operation
  isSave?: boolean
  isNew?: boolean
}
const DatesContainer = styled(Flex)`
  background-color: ${({theme}) => theme.colors.utility.promo};
  border-radius: ${({theme}) => theme.spacing.one};
  margin-top: ${({theme}) => theme.spacing.two};
`
const getHeadingLbl = (recognitionAutomation: RecognitionAutomation, operation: Operation, isSave?: boolean) => {
  if (isSave && operation === Operation.EDIT) {
    return "admin.automation.heading.automationEditedAndDraft"
  }
  if (isSave && operation === Operation.CREATE) {
    return "admin.automation.heading.automationCreatedAndDraft"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Active && operation === Operation.CREATE) {
    return "admin.automation.heading.automationCreatedAndActive"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Active && operation === Operation.EDIT) {
    return "admin.automation.heading.automationEditedAndActive"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Deactivated && operation === Operation.EDIT) {
    return "admin.automation.heading.automationEditedAndDeactivated"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Deactivated && operation === Operation.TOGGLE_STATUS) {
    return "admin.automation.heading.automationDeactivated"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Active && operation === Operation.TOGGLE_STATUS) {
    return "admin.automation.heading.automationActivated"
  }
  return ""
}
const getTrackingEventType = (recognitionAutomation: RecognitionAutomation, isSave = false): "save" | "activate" | "deactivate" => {
  if (isSave) {
    return "save"
  }
  if (recognitionAutomation?.status === RecognitionAutomationStatus.Deactivated) {
    return "deactivate"
  }
  return "activate"
}
type ExtraParams = {
  hasEmploymentStartDate: boolean
  hasDateOfBirth: boolean
}
const PopupSettings = {
  [AutomationType.Birthday]: {
    datesVariable: "hasDateOfBirth",
    title: "admin.automation.header.birthdayResults",
    detectedDates: "admin.automation.lbl.birthdaysDetected",
    partialDates: "admin.automation.msg.bdayPartialDatesMissing",
    missingDates: "admin.automation.lbl.birthdaysMissing",
    allMissingDates: "admin.automation.msg.bdayAllDatesMissing"
  },
  [AutomationType.WorkAnniversary]: {
    datesVariable: "hasEmploymentStartDate",
    title: "admin.automation.header.startDateResults",
    detectedDates: "admin.automation.lbl.detectedDates",
    partialDates: "admin.automation.msg.partialDatesMissing",
    missingDates: "admin.automation.lbl.missingDates",
    allMissingDates: "admin.automation.msg.allDatesMissing"
  }
}
const usersForBusinessVariables = (recognitionAutomation?: RecognitionAutomation, extraParams?: ExtraParams) => ({
  businessId: authentication.business().uuid,
  teamId: recognitionAutomation?.region?.uuid,
  enabled: true,
  totalOnly: true,
  ...(extraParams || {})
})
const AutomationConfirmationModal = (props: Props, context) => {
  const {operation, recognitionAutomation, isSave = false, isNew = false} = props
  const [show, setShow] = useState(false)
  const popupSettings = PopupSettings[recognitionAutomation.occasion?.automationType!]
  const variableKey = popupSettings?.datesVariable
  const [fetchUsersNoStartDate, apolloUsersNoDates] = useLazyUsersForBusiness(
    usersForBusinessVariables(recognitionAutomation, {
      [variableKey]: false
    }),
    {
      fetchPolicy: "network-only"
    }
  )
  const [fetchUsersWithStartDate, apolloUsersDates] = useLazyUsersForBusiness(
    usersForBusinessVariables(recognitionAutomation, {
      [variableKey]: true
    }),
    {
      fetchPolicy: "network-only"
    }
  )
  useEffect(() => {
    if (recognitionAutomation?.uuid && popupSettings) {
      fetchUsersNoStartDate()
      fetchUsersWithStartDate()
      setShow(true)
      adminTracking.recognitionAutomationConfirmation(
        getTrackingEventType(recognitionAutomation, isSave),
        recognitionAutomation.occasion?.automationType
      )
    }
  }, [recognitionAutomation, popupSettings])
  const onHide = () => {
    if (isNew) {
      History.replace(`${AUTOMATION_BASE}${AUTOMATION_LIST}`, context, {scrollToTop: false})
    }
    setShow(false)
  }

  const usersWithDates = apolloUsersDates?.data?.total as number
  const missingDates = apolloUsersNoDates?.data?.total as number
  const allDatesMissing = missingDates > 0 && usersWithDates === 0
  const partialDatesMissing = missingDates > 0 && usersWithDates > 0

  const handleOnClick = () => {
    History.push({pathname: VIEW_PEOPLE, state: {toggleDatesFilter: !!missingDates && missingDates > 0}}, context)
  }

  if (!recognitionAutomation || apolloUsersNoDates.loading || apolloUsersDates.loading) {
    return null
  }
  const headerLbl = getHeadingLbl(recognitionAutomation, operation, isSave)
  return (
    <GenericModal show={show} onHide={onHide} onExited={onHide} hideActions>
      <DatesContainer direction="column" padding={2}>
        <Flex alignItems="center" gap={1}>
          <Icon icon={IconTypes.circleCheckFill} />
          <H4>
            <FormattedMessage id={headerLbl} />
          </H4>
        </Flex>
        {recognitionAutomation?.status === RecognitionAutomationStatus.Active && (
          <Body small>
            <FormattedMessage id="admin.automation.lbl.automationStartSoon" />
          </Body>
        )}
      </DatesContainer>
      <H3 marginTop={3}>
        <FormattedMessage id={popupSettings?.title} />
      </H3>
      {usersWithDates === 0 && missingDates === 0 && (
        <>
          <Hr />
          <Flex alignItems="center" gap={1}>
            <Icon icon={IconTypes.circleAlertFill} />
            <H5>
              <FormattedMessage id="admin.automation.lbl.noUsersDetected" />
            </H5>
          </Flex>
        </>
      )}
      {usersWithDates > 0 && (
        <>
          <Hr />
          <Flex alignItems="center" gap={1}>
            <Icon icon={IconTypes.circleCheckFill} />
            <H5>
              <FormattedMessage id={popupSettings?.detectedDates} values={{count: usersWithDates}} />
            </H5>
          </Flex>
        </>
      )}
      {allDatesMissing && (
        <>
          <Hr />
          <Flex alignItems="center" gap={1}>
            <Icon icon={IconTypes.circleAlertFill} />
            <H5>
              <FormattedMessage id={popupSettings?.allMissingDates} />
            </H5>
          </Flex>
        </>
      )}
      {partialDatesMissing && (
        <>
          <Hr />
          <Flex alignItems="center" gap={1}>
            <Icon icon={IconTypes.circleAlertFill} />
            <H5>
              <FormattedMessage id={popupSettings?.missingDates} values={{count: missingDates}} />
            </H5>
          </Flex>
        </>
      )}
      <Hr />
      {missingDates > 0 && usersWithDates > 0 && (
        <Body small>
          <FormattedMessage id={popupSettings?.partialDates} />
        </Body>
      )}
      {allDatesMissing && (
        <Body small>
          <FormattedMessage id={popupSettings?.allMissingDates} />
        </Body>
      )}
      <Flex direction="column" gap={2} marginTop={2}>
        {!allDatesMissing && (
          <ButtonRouterLink onClick={() => setShow(false)} primary to={`${AUTOMATION_BASE}${AUTOMATION_LIST}`}>
            <FormattedMessage id="admin.automation.btn.automationPage" />
          </ButtonRouterLink>
        )}
        <Button onClick={handleOnClick} primary={allDatesMissing}>
          <FormattedMessage id="admin.automation.btn.peoplePage" />
        </Button>
      </Flex>
    </GenericModal>
  )
}
AutomationConfirmationModal.contextTypes = historyContext
export default AutomationConfirmationModal
